import { useState, useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { getFileUrl } from "@/lib/api";

/**
 * OptimizedImage - Component for lazy loading images
 * Features:
 * - Lazy loading (loads only when visible)
 * - Blur placeholder while loading
 * - Error handling with fallback
 * - Smooth fade-in animation
 */
export function OptimizedImage({ 
  src, 
  alt, 
  className, 
  fallback = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
  aspectRatio = "4/3",
  objectFit = "cover",
  ...props 
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Compute image URL using useMemo to avoid useEffect setState issues
  const imageSrc = useMemo(() => {
    if (!src || hasError) {
      return fallback;
    }
    
    // If it's already a full URL, use it directly
    if (src.startsWith('http://') || src.startsWith('https://')) {
      return src;
    }
    
    // Otherwise, construct the URL using getFileUrl
    return getFileUrl(src) || fallback;
  }, [src, hasError, fallback]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "200px",
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    console.warn('Image failed to load:', imageSrc);
    if (!hasError) {
      setHasError(true);
    }
    setIsLoaded(true);
  };

  return (
    <div 
      ref={imgRef}
      className={cn(
        "relative overflow-hidden bg-secondary",
        className
      )}
      style={{ aspectRatio }}
      {...props}
    >
      {/* Placeholder skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-secondary animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/50 to-secondary animate-shimmer" />
        </div>
      )}
      
      {/* Actual image - only load when in view */}
      {isInView && imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
          className={cn(
            "absolute inset-0 w-full h-full transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
            objectFit === "cover" && "object-cover",
            objectFit === "contain" && "object-contain"
          )}
        />
      )}
    </div>
  );
}

/**
 * ImageGrid - Optimized grid for displaying multiple images
 */
export function ImageGrid({ images, onImageClick, className }) {
  if (!images || images.length === 0) return null;

  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-3 gap-2", className)}>
      {images.map((image, index) => (
        <button
          key={index}
          onClick={() => onImageClick?.(index)}
          className="relative group rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <OptimizedImage
            src={image}
            alt={`Image ${index + 1}`}
            className="w-full"
            aspectRatio="4/3"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
        </button>
      ))}
    </div>
  );
}

/**
 * Utility function for compressing images before upload
 */
export async function compressImageFile(file, options = {}) {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.75,
    maxSizeMB = 1.5
  } = options;

  return new Promise((resolve, reject) => {
    // Skip if already small enough
    if (file.size < maxSizeMB * 1024 * 1024) {
      console.log(`Image already small: ${(file.size/1024).toFixed(0)}KB`);
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              
              console.log(`Compressed: ${(file.size/1024).toFixed(0)}KB → ${(blob.size/1024).toFixed(0)}KB`);
              resolve(compressedFile);
            } else {
              reject(new Error('Compression failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
}

/**
 * Batch compress multiple images
 */
export async function compressImages(files, options = {}, onProgress) {
  const results = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const compressed = await compressImageFile(files[i], options);
      results.push(compressed);
      onProgress?.((i + 1) / files.length * 100);
    } catch (error) {
      console.error(`Failed to compress ${files[i].name}:`, error);
      results.push(files[i]);
    }
  }
  
  return results;
}
